import {
  applicationStatusMapping,
  dealStageIdMapping,
  TrackingEventNameEnum,
} from '@/helpers/analytics';

import { getRawUrl } from '@/helpers/location';
import { MicroserviceEnum, Mutation } from '@/helpers/westeros';
import { trackEvent, TrackingEventInput } from '@/store/graphql/mutations/westerosMutations';
import RootState from '@/store/root_state';
import { ActionTree } from 'vuex';
import { UserInfo } from '../user/state';
import { extractNamesByFullName } from '@/helpers/names';

export interface TrackEventPayload {
  eventName: string;
  eventData: { [key: string]: any };
  identify?: TrackEventIdentify;
}

interface TrackEventIdentify {
  id: string;
  email: string;
  name: string;
}

export const AnalyticsActions = {
  TRACK_EVENT: 'TRACK_EVENT',

  SUBSCRIPTION_COMPLETED: 'SUBSCRIPTION_COMPLETED',
  COURSE_CHOSEN: 'COURSE_CHOSEN',
  CITY_CHOSEN: 'CITY_CHOSEN',
  POLO_CHOSEN: 'POLO_CHOSEN',
  EVALUATION_TYPE_CHOSEN: 'EVALUATION_TYPE_CHOSEN',
  EVALUATION_SENT: 'EVALUATION_SENT',
  ESSAY_EVALUATION_SENT: 'ESSAY_EVALUATION_SENT',
  USER_APPLICATION_STATUS: 'USER_APPLICATION_STATUS',
  REQUEST_NEW_SIGN_IN_CODE: 'REQUEST_NEW_SIGN_IN_CODE',
  PAGE_VIEW: 'PAGE_VIEW',
  SCREEN_VIEWED: 'SCREEN_VIEWED',
};

const userInfo = (user: UserInfo) => {
  const { email, name } = user;
  return { id: email, email, name };
};

export const ESSAY_TEXTS = {
  ESSAY: 'Redação',
  ESSAY_TYPE_PHOTO: 'Redação foto',
  ESSAY_TYPE_FILE: 'Redação arquivo',
  ESSAY_TYPE_TYPING: 'Redação digitada',
};

const actionsFactory: (mutation: Mutation<any>) => ActionTree<{}, RootState> = mutation => {
  const areTrackingsEnabled = JSON.parse(process.env.VUE_APP_ENABLE_TRACKINGS || 'false');

  return {
    async TRACK_EVENT(_, payload: TrackEventPayload): Promise<void> {
      if (!areTrackingsEnabled) {
        return;
      }

      const eventInput: TrackingEventInput = {
        data: {
          ...payload,
          eventData: {
            ...window.mixpanel?.cookie?.props,
            ...payload.eventData,
          },
        },
      };

      const { data } = await mutation(MicroserviceEnum.ADMISSIONS, trackEvent, eventInput);

      if (!data?.trackEvent.success) {
        throw new Error('Tracking Service unable to track event');
      }
    },

    async SUBSCRIPTION_COMPLETED({ dispatch, rootGetters }) {
      const { firstName, lastName } = extractNamesByFullName(rootGetters.user?.name);
      dispatch(AnalyticsActions.TRACK_EVENT, {
        eventName: TrackingEventNameEnum.SUBSCRIPTION_COMPLETED,
        eventData: {
          firstName,
          lastName,
          phone: rootGetters.user?.phone,
          email: rootGetters.user?.email,
        },
        identify: userInfo(rootGetters.user),
      });
    },

    async COURSE_CHOSEN({ dispatch, rootGetters }, payload) {
      dispatch(AnalyticsActions.TRACK_EVENT, {
        eventName: TrackingEventNameEnum.COURSE_CHOSEN,
        eventData: {
          ...payload,
          email: rootGetters.user?.email,
          processoSeletivoId: rootGetters.admissionId,
        },
        identify: userInfo(rootGetters.user),
      });
    },

    async CITY_CHOSEN({ dispatch, rootGetters }, payload: any) {
      dispatch(AnalyticsActions.TRACK_EVENT, {
        eventName: TrackingEventNameEnum.CITY_CHOSEN,
        eventData: {
          ...payload,
          processoSeletivoId: rootGetters.admissionId,
          email: rootGetters.user?.email,
        },
        identify: userInfo(rootGetters.user),
      });
    },

    async POLO_CHOSEN({ dispatch, rootGetters }, payload: any) {
      dispatch(AnalyticsActions.TRACK_EVENT, {
        eventName: TrackingEventNameEnum.POLO_CHOSEN,
        eventData: {
          ...payload,
          processoSeletivoId: rootGetters.admissionId,
          email: rootGetters.user?.email,
        },
        identify: userInfo(rootGetters.user),
      });
    },

    async EVALUATION_TYPE_CHOSEN({ dispatch, rootGetters }, payload: any) {
      dispatch(AnalyticsActions.TRACK_EVENT, {
        eventName: TrackingEventNameEnum.EVALUATION_TYPE_CHOSEN,
        eventData: { ...payload },
        identify: userInfo(rootGetters.user),
      });
    },

    async EVALUATION_SENT({ dispatch, rootGetters }, payload?) {
      dispatch(AnalyticsActions.TRACK_EVENT, {
        eventName: TrackingEventNameEnum.EVALUATION_SENT,
        eventData: {
          processoSeletivoId: rootGetters.admissionId,
          email: rootGetters.user?.email,
          evaluationTypeSent: rootGetters.selectedAdmissionType?.name,
          grad_processo_seletivo: rootGetters.selectedAdmissionType?.name,
          ...payload,
        },
        identify: userInfo(rootGetters.user),
      });
    },

    async ESSAY_EVALUATION_SENT({ dispatch }, { uploadType, fileMimeType }) {
      let evaluationTypeSent = ESSAY_TEXTS.ESSAY_TYPE_FILE;
      if (uploadType === 'text') {
        evaluationTypeSent = ESSAY_TEXTS.ESSAY_TYPE_TYPING;
      } else if (fileMimeType.includes('image/')) {
        evaluationTypeSent = ESSAY_TEXTS.ESSAY_TYPE_PHOTO;
      }

      dispatch(AnalyticsActions.EVALUATION_SENT, {
        evaluationTypeSent,
        grad_processo_seletivo: ESSAY_TEXTS.ESSAY,
      });
    },

    async USER_APPLICATION_STATUS({ dispatch, rootGetters }) {
      const { evaluationStatus } = rootGetters;

      dispatch(AnalyticsActions.TRACK_EVENT, {
        eventName: TrackingEventNameEnum.USER_APPLICATION_STATUS,
        eventData: {
          processoSeletivoId: rootGetters.admissionId,
          email: rootGetters.user?.email,
          applicationStatus: applicationStatusMapping[evaluationStatus],
          lead_status: applicationStatusMapping[evaluationStatus],
          deal_stage: dealStageIdMapping[evaluationStatus],
        },
        identify: userInfo(rootGetters.user),
      });
    },

    async REQUEST_NEW_SIGN_IN_CODE(
      { dispatch, rootGetters },
      { selectedChannel, previousNotificationChannel },
    ) {
      dispatch(AnalyticsActions.TRACK_EVENT, {
        eventName: TrackingEventNameEnum.REQUEST_NEW_SIGN_IN_CODE,
        eventData: {
          previousNotificationChannel,
          newNotificationChannel: selectedChannel || previousNotificationChannel,
        },
        identify: userInfo(rootGetters.user),
      });
    },

    async PAGE_VIEW({ dispatch, rootGetters }) {
      dispatch(AnalyticsActions.TRACK_EVENT, {
        eventName: TrackingEventNameEnum.PAGE_VIEW,
        eventData: {
          gt_Location: getRawUrl(),
        },
        identify: userInfo(rootGetters.user),
      });
    },

    async SCREEN_VIEWED({ dispatch, rootGetters }) {
      dispatch(AnalyticsActions.TRACK_EVENT, {
        eventName: TrackingEventNameEnum.SCREEN_VIEWED,
        eventData: {
          path: getRawUrl(),
          email: rootGetters.user?.email,
        },
        identify: userInfo(rootGetters.user),
      });
    },
  };
};

export default actionsFactory;
